import React, { useState, useEffect } from 'react';
import { db, storage } from '../../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import '../CompanyHistory.css';

const CertificationManagement = () => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCert, setEditingCert] = useState(null);
  
  const [formData, setFormData] = useState({
    category: '',
    name: '',
    image: '',
    pdf: '',
    imageUrl: '',
    pdfUrl: '',
    order: 0
  });

  // Firebase에서 데이터 불러오기
  const fetchCertificates = async () => {
    try {
      setLoading(true);
      const certificatesCollection = collection(db, 'certificates');
      const certificatesSnapshot = await getDocs(certificatesCollection);
      
      const certificatesData = certificatesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // order 기준으로 정렬
      certificatesData.sort((a, b) => a.order - b.order);
      
      setCertificates(certificatesData);
    } catch (error) {
      console.error('Error loading certificate data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCertificates();
  }, []);

  // 폼 데이터 초기화
  const resetForm = () => {
    setFormData({
      category: '',
      name: '',
      image: '',
      pdf: '',
      imageUrl: '',
      pdfUrl: '',
      order: 0
    });
    setEditingCert(null);
    setShowAddForm(false);
  };

  const uploadFile = async ({ file, folder }) => {
    const safeName = (file?.name || 'file').replace(/[^\w.\-() ]/g, '_');
    const path = `certificates/${folder}/${Date.now()}_${safeName}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file, { contentType: file.type || undefined });
    const url = await getDownloadURL(storageRef);
    return { url, path };
  };

  // 인증서 추가
  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const certificatesCollection = collection(db, 'certificates');
      await addDoc(certificatesCollection, formData);
      console.log('Certificate added successfully.');
      resetForm();
      fetchCertificates();
    } catch (error) {
      console.error('Error adding certificate:', error);
    }
  };

  // 인증서 수정
  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      const certRef = doc(db, 'certificates', editingCert.id);
      await updateDoc(certRef, formData);
      console.log('Certificate updated successfully.');
      resetForm();
      fetchCertificates();
    } catch (error) {
      console.error('Error updating certificate:', error);
    }
  };

  // 인증서 삭제
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this certificate?')) {
      try {
        await deleteDoc(doc(db, 'certificates', id));
        console.log('Certificate deleted successfully.');
        fetchCertificates();
      } catch (error) {
        console.error('Error deleting certificate:', error);
      }
    }
  };

  // 수정 모드로 전환
  const handleEditClick = (certificate) => {
    setEditingCert(certificate);
    setFormData({
      category: certificate.category,
      name: certificate.name,
      image: certificate.image,
      pdf: certificate.pdf,
      imageUrl: certificate.imageUrl || '',
      pdfUrl: certificate.pdfUrl || '',
      order: certificate.order
    });
    setShowAddForm(true);
  };

  if (loading) {
    return (
      <div className="company-history-page">
        <div className="history-container">
          <h1 className="page-category-title">Certificate Management</h1>
          <div className="section-container">
            <p>Loading data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="company-history-page">
      <div className="history-container">
        <h1 className="page-category-title">Certificate Management</h1>
        
        {/* 추가/수정 폼 */}
        {showAddForm && (
          <div className="section-container">
            <h2>{editingCert ? 'Edit Certificate' : 'Add Certificate'}</h2>
            <form onSubmit={editingCert ? handleEdit : handleAdd} className="certificate-form">
              <div className="form-group">
                <label>Category:</label>
                <select 
                  value={formData.category} 
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  required
                >
                  <option value="">Select Category</option>
                  <option value="ISO 인증서 (한글)">ISO Certificates (Korean)</option>
                  <option value="ISO 인증서 (영문)">ISO Certificates (English)</option>
                  <option value="ASME 인증서">ASME Certificates</option>
                  <option value="KC 인증서">KC Certificates</option>
                  <option value="특허권">Patents</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Certificate Name:</label>
                <input 
                  type="text" 
                  value={formData.name} 
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Image (Upload recommended)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const { url } = await uploadFile({ file, folder: 'images' });
                      setFormData((prev) => ({
                        ...prev,
                        image: file.name,
                        imageUrl: url,
                      }));
                    } catch (error) {
                      console.error('Image upload failed:', error);
                      alert('Image upload failed.');
                    }
                  }}
                />
                <div style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
                  Current URL: {formData.imageUrl ? formData.imageUrl : '(none)'}
                </div>
              </div>
              
              <div className="form-group">
                <label>PDF (Upload recommended)</label>
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const { url } = await uploadFile({ file, folder: 'pdfs' });
                      setFormData((prev) => ({
                        ...prev,
                        pdf: file.name,
                        pdfUrl: url,
                      }));
                    } catch (error) {
                      console.error('PDF upload failed:', error);
                      alert('PDF upload failed.');
                    }
                  }}
                />
                <div style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
                  Current URL: {formData.pdfUrl ? formData.pdfUrl : '(none)'}
                </div>
              </div>
              
              <div className="form-group">
                <label>Order:</label>
                <input 
                  type="number" 
                  value={formData.order} 
                  onChange={(e) => setFormData({...formData, order: parseInt(e.target.value)})}
                  required
                />
              </div>
              
              <div className="form-actions">
                <button type="submit">{editingCert ? 'Update' : 'Add'}</button>
                <button type="button" onClick={resetForm}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* 인증서 목록 */}
        <div className="section-container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
            <h2>Certificate List</h2>
            <button onClick={() => setShowAddForm(true)} className="add-button">
              Add New Certificate
            </button>
          </div>
          
          <div className="certificate-management-list">
            {certificates.map((cert) => (
              <div key={cert.id} className="certificate-management-item">
                <div className="certificate-preview">
                  <img
                    src={cert.imageUrl || `/${cert.image}`}
                    alt={cert.name}
                    className="certificate-thumbnail"
                  />
                </div>
                <div className="certificate-details">
                  <h3>{cert.name}</h3>
                  <p><strong>Category:</strong> {cert.category}</p>
                  <p><strong>Image:</strong> {cert.image}</p>
                  <p><strong>PDF:</strong> {cert.pdf}</p>
                  {cert.imageUrl && <p><strong>Image URL:</strong> {cert.imageUrl}</p>}
                  {cert.pdfUrl && <p><strong>PDF URL:</strong> {cert.pdfUrl}</p>}
                  <p><strong>Order:</strong> {cert.order}</p>
                </div>
                <div className="certificate-actions">
                  <button onClick={() => handleEditClick(cert)} className="edit-button">
                    Edit
                  </button>
                  <button onClick={() => handleDelete(cert.id)} className="delete-button">
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificationManagement;
